import type { Company, SortKey } from '@/constants/companies'

import { createStore } from 'zustand/vanilla'
import { NOTIFICATIONS } from '@/constants/notifications'
import { DEFAULT_FILTERS } from '@/lib/companies'

import type {
    Activity,
    Contact,
    CrmData,
    Deal,
    Sequence
} from '@/constants/crm'

export type CrmStatus = 'loading' | 'ready' | 'error'

export type DialogName = 'company' | 'deal' | 'contact' | 'activity' | 'sequence' | 'invite'

export type DialogDefaults = {
    companyId?: string
    contactId?: string
    stage?: string
    pipeline?: string
    dealId?: string
}

export type Notice = {
    id: number
    tone: 'success' | 'error'
    message: string
}

export type CrmState = CrmData & {
    today: string
    status: CrmStatus
    sortBy: SortKey
    owner: string
    stage: string
    activityWindow: number
    selectedIds: string[]
    detailId: string | null
    detailOpen: boolean
    profileName: string | null
    profileOpen: boolean
    contactId: string | null
    sequenceId: string | null
    dialog: DialogName | null
    dialogDefaults: DialogDefaults
    sidebarOpen: boolean
    searchOpen: boolean
    unreadNotificationIds: string[]
    notice: Notice | null
    setData: (data: CrmData) => void
    setStatus: (status: CrmStatus) => void
    setSortBy: (sortBy: SortKey) => void
    setOwner: (owner: string) => void
    setStage: (stage: string) => void
    setActivityWindow: (days: number) => void
    resetFilters: () => void
    toggleSelected: (id: string) => void
    setSelected: (ids: string[]) => void
    openDetail: (id: string) => void
    closeDetail: () => void
    openProfile: (name: string) => void
    closeProfile: () => void
    openContact: (id: string) => void
    closeContact: () => void
    openSequence: (id: string) => void
    closeSequence: () => void
    openDialog: (name: DialogName, defaults?: DialogDefaults) => void
    closeDialog: () => void
    setSidebarOpen: (open: boolean) => void
    setSearchOpen: (open: boolean) => void
    markNotificationRead: (id: string) => void
    markAllNotificationsRead: () => void
    addCompany: (company: Company) => void
    upsertDeal: (deal: Deal) => void
    upsertContact: (contact: Contact) => void
    upsertActivity: (activity: Activity) => void
    upsertSequence: (sequence: Sequence) => void
    showNotice: (tone: Notice['tone'], message: string) => void
    clearNotice: () => void
}

export type CrmStoreInit = {
    data: CrmData | null
    today: string
}

const DEFAULT_SELECTED_SLUG = 'microsoft'

const EMPTY_DATA: CrmData = {
    companies: [],
    deals: [],
    contacts: [],
    activities: [],
    sequences: []
}

function defaultSelection(companies: Company[]): string[] {
    return companies
        .filter(company => company.slug === DEFAULT_SELECTED_SLUG)
        .map(company => company.id)
}

function upsert<T extends { id: string }>(items: T[], item: T, position: 'start' | 'end'): T[] {
    if (items.some(existing => existing.id === item.id)) {
        return items.map(existing => existing.id === item.id ? item : existing)
    }

    return position === 'start' ? [item, ...items] : [...items, item]
}

export const createCrmStore = ({ data, today }: CrmStoreInit) => createStore<CrmState>()(set => ({
    ...(data ?? EMPTY_DATA),
    today,
    status: data ? 'ready' : 'loading',
    ...DEFAULT_FILTERS,
    selectedIds: defaultSelection(data?.companies ?? []),
    detailId: null,
    detailOpen: false,
    profileName: null,
    profileOpen: false,
    contactId: null,
    sequenceId: null,
    dialog: null,
    dialogDefaults: {},
    sidebarOpen: false,
    searchOpen: false,
    unreadNotificationIds: NOTIFICATIONS
        .filter(item => item.unread)
        .map(item => item.id),
    notice: null,
    setData: next => set(state => ({
        ...next,
        status: 'ready',
        selectedIds: state.selectedIds.length > 0 ? state.selectedIds : defaultSelection(next.companies)
    })),
    setStatus: status => set({ status }),
    setSortBy: sortBy => set({ sortBy }),
    setOwner: owner => set({ owner }),
    setStage: stage => set({ stage }),
    setActivityWindow: activityWindow => set({ activityWindow }),
    resetFilters: () => set({ ...DEFAULT_FILTERS }),
    toggleSelected: id => set(state => ({
        selectedIds: state.selectedIds.includes(id)
            ? state.selectedIds.filter(selected => selected !== id)
            : [...state.selectedIds, id]
    })),
    setSelected: selectedIds => set({ selectedIds }),
    openDetail: detailId => set({ detailId, detailOpen: true, profileOpen: false, contactId: null, sequenceId: null }),
    closeDetail: () => set({ detailOpen: false }),
    openProfile: profileName => set({ profileName, profileOpen: true, detailOpen: false, contactId: null, sequenceId: null }),
    closeProfile: () => set({ profileOpen: false }),
    openContact: contactId => set({ contactId, detailOpen: false, profileOpen: false, sequenceId: null }),
    closeContact: () => set({ contactId: null }),
    openSequence: sequenceId => set({ sequenceId, detailOpen: false, profileOpen: false, contactId: null }),
    closeSequence: () => set({ sequenceId: null }),
    openDialog: (dialog, dialogDefaults = {}) => set({ dialog, dialogDefaults }),
    closeDialog: () => set({ dialog: null }),
    setSidebarOpen: sidebarOpen => set({ sidebarOpen }),
    setSearchOpen: searchOpen => set({ searchOpen }),
    markNotificationRead: id => set(state => ({
        unreadNotificationIds: state.unreadNotificationIds.filter(unread => unread !== id)
    })),
    markAllNotificationsRead: () => set({ unreadNotificationIds: [] }),
    addCompany: company => set(state => ({
        companies: [company, ...state.companies],
        status: 'ready',
        dialog: null
    })),
    upsertDeal: deal => set(state => ({ deals: upsert(state.deals, deal, 'end') })),
    upsertContact: contact => set(state => ({ contacts: upsert(state.contacts, contact, 'start') })),
    upsertActivity: activity => set(state => ({ activities: upsert(state.activities, activity, 'end') })),
    upsertSequence: sequence => set(state => ({ sequences: upsert(state.sequences, sequence, 'end') })),
    showNotice: (tone, message) => set({ notice: { id: Date.now(), tone, message } }),
    clearNotice: () => set({ notice: null })
}))

export type CrmStore = ReturnType<typeof createCrmStore>
